import {
  CreditsPackage,
  PremiumPackage,
} from '@project-gd-x/dating-site-contracts/src/gen/gdx/gateway/web/payment/v2/package_pb';

import { primerPurchaseToken } from '@/components/payment/components/payment-method/components/primer/primer-payment-method/helpers/primer/primer';
import { usePayment } from '@/components/payment/hooks/use-payment/use-payment';
import { memoize } from '@/helpers/memoize/memoize';
import { trackPaymentClick } from '@/helpers/track/track-payment/track-payment';
import PaymentMethod from '@/pages/payment/components/payment-method/PaymentMethod.vue';
import { handlePayError } from '@/pages/payment/components/purchase/helpers/purchase-helper/purchase-helper';
import { PaymentStateCard, PaymentStatePackage } from '@/pages/payment/store/payment-state';
import { replaceRoute } from '@/router';
import { useSplit } from '@/services/growth-book/hooks/use-split/use-split';
import { hideAllModals, useModal } from '@/services/modal/modal';
import { commit, dispatch, getState } from '@/store/store-helper';
import { ExtractClassFields } from '@/type';

export type PayWithCard = {
  pkg: ExtractClassFields<PremiumPackage> | ExtractClassFields<CreditsPackage>;
  cardId: string;
};

export function setLastPurchasedPackage(pkg: PaymentStatePackage): void {
  commit('mutationPaymentSetLastPurchasedPackage', pkg);
}

export async function payWithCard({ cardId, pkg }: PayWithCard) {
  setLastPurchasedPackage(pkg);
  try {
    trackPaymentClick({
      method: 'token',
    });

    const splitPrimer = useSplit('primer');
    await splitPrimer.init();
    const cards = await memoize(loadCards, {
      removeCacheOnResolved: true,
    })();
    const card = cards.find((c) => c.id === cardId) ?? cards[0];

    // We can't process PayPal with primer, so we need to use the Solid payment method.
    if (splitPrimer.isV2 && card.brand !== 'PayPal') {
      // TODO remove when we be sure that primer works without cvv.
      // primerCvvModal.show({
      //   card,
      //   onSubmit: (cvv: string) => {
      //     primerPurchaseToken({
      //       pkg: {
      //         id: pkg.id,
      //         offerId: pkg.offer?.id ?? '',
      //       },
      //       cvv,
      //       card,
      //     });
      //   },
      // });
      primerPurchaseToken({
        pkg: {
          id: pkg.id,
          offerId: pkg.offer?.id ?? '',
        },
        card,
      });
    } else {
      await dispatch('actionPaymentPayByCardId', {
        cardId,
        pkg: {
          id: pkg.id,
          offerId: pkg.offer?.id ?? '',
        },
      });
    }
  } catch (e) {
    handlePayError(e);
  }
}

export async function loadCards() {
  await dispatch('actionPaymentListPaymentCards');
  return getState().payment.paymentCards;
}

export function paymentListenResult({
  onSuccess,
  onFail,
}: {
  onSuccess?: () => void;
  onFail?: () => void;
}) {
  usePayment({
    paymentSuccess: () => {
      if (onSuccess) {
        onSuccess();
      } else {
        replaceRoute('paymentSuccess');
        hideAllModals();
      }
    },
    paymentFail: () => {
      if (onFail) {
        onFail();
      } else {
        replaceRoute('paymentFail');
        hideAllModals();
      }
    },
  });
}

type PaymentParams = {
  pkg: PaymentStatePackage;
  onClose?: () => void;
  onSuccess?: () => void;
  onFail?: () => void;
  isCardFormForced?: boolean;
};

export async function paymentPurchase({
  pkg,
  onClose = () => ({}),
  onSuccess,
  onFail,
  isCardFormForced = false,
}: PaymentParams) {
  await dispatch('actionPaymentListPaymentMethods');

  const cards = await memoize(loadCards, {
    removeCacheOnResolved: true,
  })();
  const defaultPaymentCard = cards[0];

  if (defaultPaymentCard) {
    paymentListenResult({
      onSuccess,
      onFail,
    });
    await payWithCard({
      cardId: defaultPaymentCard.id,
      pkg,
    });
  } else {
    await paymentSelect({ pkg, onClose, onSuccess, onFail, isCardFormForced });
  }
}

export async function paymentSelect({
  pkg,
  onClose: close = () => ({}),
  onSuccess,
  onFail,
  forceAddNewMethod = false,
  isCardFormForced = false,
}: {
  pkg: PaymentStatePackage;
  onClose?: () => void;
  onSuccess?: () => void;
  onFail?: () => void;
  forceAddNewMethod?: boolean;
  isCardFormForced?: boolean;
}) {
  await dispatch('actionPaymentListPaymentMethods');

  setLastPurchasedPackage(pkg);
  let cards: PaymentStateCard[] = [];
  if (!forceAddNewMethod) {
    cards = await loadCards();
  }
  paymentListenResult({
    onSuccess,
    onFail,
  });

  const { showModal } = useModal(() => ({
    component: PaymentMethod,
    props: {
      pkg,
      cards,
      isCardFormForced,
    },
    on: {
      close,
    },
  }));
  showModal();
}
